/**
 * OwnerPaymentHistoryInfinite.jsx
 * ANALOGIE EXACTE de PaymentHistoryInfinite.jsx mais du point de vue OWNER
 * Affiche l'historique des REVENUS reçus sur les réservations des listings de l'owner
 */

import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Collapse,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  LinearProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  MonetizationOn as MonetizationOnIcon,
  Lock as LockIcon,
  Assignment as AssignmentIcon,
  AccountBalance as RefundIcon,
  Gavel as GavelIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatCurrency, formatDateTimeUniversal } from '../../utils/formatters';
import { formatDate } from '../../utils/formatters';
import { paymentService } from '../../services/paymentService';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Import du composant modal (réutilisé)
import ReservationDetailsModal from './ReservationDetailsModal';

// Import du composant DateRangePicker
import DateRangePicker from '../common/DateRangePicker';

// Import du composant PenaltyExecutionSummary
import PenaltyExecutionSummary from '../disputes/PenaltyExecutionSummary';

// Import des styles CSS (réutilisés)
import './PaymentHistoryInfinite.css';

// Fonction pour formater la date avec l'heure
const formatDateWithTime = (dateString, t) => {
  if (!dateString) return t ? t('financial:dateUnknown', 'Date inconnue') : 'Date inconnue';
  
  try {
    const date = new Date(dateString);
    
    // Format DD/MM/YYYY HH:MM
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return t ? t('financial:dateInvalid', 'Date invalide') : 'Date invalide';
  }
};

// Fonction pour traduire les statuts de réservation
const getReservationStatusLabel = (status, t) => {
  if (t) {
    const key = `financial:reservationStatuses.${status}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const statusLabels = {
    'active': 'Active',
    'completed': 'Terminée', 
    'cancelled': 'Annulée',
    'pending': 'En attente',
    'confirmed': 'Confirmée'
  };
  return statusLabels[status] || status || 'Inconnu';
};

// Fonction pour obtenir la couleur et l'icône du statut de réservation
const getReservationStatusProps = (status) => {
  const statusProps = {
    'active': { color: 'primary', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
    'completed': { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    'cancelled': { color: 'error', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
    'pending': { color: 'warning', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
    'confirmed': { color: 'info', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> }
  };
  return statusProps[status] || { color: 'default', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> };
};

// Fonction pour extraire l'ID du litige (plusieurs formats possibles)
const getDisputeId = (dispute) => {
  if (!dispute) return null;
  return dispute.id || dispute._id || dispute.dispute_id || dispute.disputeId || null;
};

// Fonction pour obtenir le label du type de compensation
const getCompensationTypeLabel = (compensationType, t) => {
  if (t) {
    const key = `financial:dispute.compensationTypes.${compensationType}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const labels = {
    'owner_compensation': '🏠 Compensation Propriétaire',
    'renter_refund': '💰 Remboursement Locataire',
    'reservation_cancellation': '❌ Annulation de Réservation'
  };
  return labels[compensationType] || compensationType || 'Inconnu';
};

const calculatePenaltyImpactForUser = (penalties = [], currentUserId = null) => {
  if (!Array.isArray(penalties) || penalties.length === 0 || !currentUserId) {
    return { positive: 0, negative: 0, net: 0 };
  }

  return penalties.reduce((summary, penalty) => {
    const executionMetadata = penalty?.execution_metadata || {};
    const victimCompensation = executionMetadata?.victim_compensation || {};
    const collectedAmount = parseFloat(executionMetadata?.total_collected ?? penalty?.amount ?? 0) || 0;
    const victimAmount = parseFloat(victimCompensation?.amount ?? 0) || 0;

    if (penalty?.user_id === currentUserId) {
      summary.negative += collectedAmount;
    }

    if (victimCompensation?.victim_user_id === currentUserId) {
      summary.positive += victimAmount || collectedAmount;
    }

    summary.net = summary.positive - summary.negative;
    return summary;
  }, { positive: 0, negative: 0, net: 0 });
};

// Fonction pour calculer le montant net ajusté en fonction du type de compensation
const calculateAdjustedNetAmount = (netAmount, dispute, penalties = [], currentUserId = null) => {
  let adjustedNet = parseFloat(netAmount) || 0;
  const compensation = parseFloat(dispute?.compensation_amount) || 0;

  console.log('🧮 calculateAdjustedNetAmount DEBUG:', {
    netAmount,
    compensation_amount: dispute?.compensation_amount,
    adjustedNet,
    compensation,
    compensation_type: dispute?.compensation_type,
    penaltiesCount: Array.isArray(penalties) ? penalties.length : 0,
    currentUserId
  });

  switch (dispute?.compensation_type) {
    case 'owner_compensation':
      adjustedNet += compensation;
      break;
    case 'renter_refund':
      adjustedNet -= compensation;
      break;
    case 'reservation_cancellation':
      adjustedNet = 0;
      break;
    default:
      break;
  }

  return adjustedNet + calculatePenaltyImpactForUser(penalties, currentUserId).net;
};

// Fonction pour traduire les statuts de litige
const getDisputeStatusLabel = (status, t) => {
  if (t) {
    const key = `financial:dispute.statusShow.${status}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const labels = {
    'pending': '⏳ En attente',
    'in_review': '🔍 En révision',
    'resolved': '✅ Résolu',
    'cancelled': '❌ Annulé',
    'needs_support': '🆘 Support nécessaire',
    'escalated': '⚠️ Escaladé',
    'closed': '🔒 Fermé',
    'pending_settlement': '⏳ Règlement en attente'
  };
  return labels[status] || status || 'Inconnu';
};

// Fonction pour obtenir la couleur du statut de litige
const getDisputeStatusColor = (status) => {
  const colorMap = {
    'pending': 'info',
    'in_review': 'warning',
    'resolved': 'success',
    'cancelled': 'error',
    'needs_support': 'warning',
    'escalated': 'warning',
    'closed': 'default',
    'pending_settlement': 'info'
  };
  return colorMap[status] || 'default';
};

// Fonction pour traduire les statuts de dépôt
const getDepositStatusLabel = (status, t) => {
  if (t) {
    const key = `financial:status.${status?.toLowerCase?.()}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const statusLabels = {
    'released': 'Libéré',
    'held': 'Retenu',
    'collected': 'Collecté',
    'refunded': 'Remboursé',
    'paid': 'Payé',
    'cancelled': 'Annulé',
    'pending': 'En attente',
  };
  return statusLabels[status] || status || 'Inconnu';
};

// Fonction pour obtenir la couleur et l'icône du statut de dépôt
const getDepositStatusProps = (status) => {
  const statusProps = {
    'RELEASED': { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    'HELD': { color: 'warning', icon: <LockIcon sx={{ fontSize: 16 }} /> },
    'COLLECTED': { color: 'error', icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
    'PENDING': { color: 'info', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
    'CANCELLED': { color: 'error', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
    'REFUNDED': { color: 'secondary', icon: <RefundIcon sx={{ fontSize: 16 }} /> },
    'paid': { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    'cancelled': { color: 'error', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
    'pending': { color: 'info', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
    'refunded': { color: 'secondary', icon: <RefundIcon sx={{ fontSize: 16 }} /> }
  };
  return statusProps[status] || { color: 'default', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> };
};

// Fonction pour extraire les filtres backend à partir des filtres locaux
const buildBackendQueryFromLocalFilters = (lf) => {
  // Owner backend supports: payment_method, reservation_status, date_period/date_start/date_end
  // We only include backend-supported filters here. Everything else stays frontend-only.
  const query = {};

  const paymentMethod = lf?.paymentMethod?.toString?.().trim?.() || lf?.paymentMethod;
  if (paymentMethod) query.paymentMethod = String(paymentMethod).toLowerCase();

  const reservationStatus = lf?.reservationStatus?.toString?.().trim?.() || lf?.reservationStatus;
  if (reservationStatus) query.reservationStatus = String(reservationStatus).toLowerCase();

  // Backend can handle custom date range. We fill missing bound with min/max like renter.
  if (lf?.dateFrom || lf?.dateTo) {
    const start = lf?.dateFrom || '1970-01-01';
    const end = lf?.dateTo || new Date().toISOString().slice(0, 10);
    query.dateRange = { start, end };
  }

  return query;
};

const serializeBackendQuery = (query) => {
  const entries = Object.entries(query || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  entries.sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(Object.fromEntries(entries));
};

const OwnerPaymentHistoryInfinite = forwardRef(({ filters = {}, onError, disableAutoLoad = false }, ref) => {
  const { t } = useTranslation(['financial', 'common','disputes']);
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  
  // États principaux - ARCHITECTURE HYBRIDE OPTIMISÉE
  const [allRevenues, setAllRevenues] = useState([]); // Cache en mémoire (limité)
  const [filteredRevenues, setFilteredRevenues] = useState([]); // Données filtrées côté frontend
  const [displayedRevenues, setDisplayedRevenues] = useState([]); // Données affichées avec pagination
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // États pour le chargement backend par chunks
  const [backendPage, setBackendPage] = useState(1);
  const [hasMoreBackend, setHasMoreBackend] = useState(true);
  
  // États pour les détails et expansions
  const [expandedRevenues, setExpandedRevenues] = useState({});
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [disputesByReservation, setDisputesByReservation] = useState({});
  const [penaltiesByReservation, setPenaltiesByReservation] = useState({});
  const [penaltiesRetryByReservation, setPenaltiesRetryByReservation] = useState({});
  const [loadingDisputes, setLoadingDisputes] = useState({});
  
  // États pour les filtres
  const [activeFilters, setActiveFilters] = useState({});
  const [availableFilterValues, setAvailableFilterValues] = useState({});
  
  // États pour les filtres locaux (côté frontend)
  const [localFilters, setLocalFilters] = useState({
    status: '',
    paymentMethod: '',
    paymentType: '',
    currency: '',
    reservationStatus: '',
    dateFrom: '',
    dateTo: '',
    ...filters
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // États pour les options de filtres dynamiques
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availablePaymentTypes, setAvailablePaymentTypes] = useState([]);

  // Refs pour la gestion du composant
  const hasInitialized = useRef(false);
  const abortControllerRef = useRef(null);
  const retryCount = useRef(0);
  const backendQueryRef = useRef({}); // Stocke la requête backend courante
  const backendQueryKeyRef = useRef('');
  
  // Configuration
  const FRONTEND_PAGE_SIZE = 5; // Affichage par page côté frontend
  const BACKEND_CHUNK_SIZE = 20; // Chargement par chunk depuis le backend
  const MAX_CACHE_SIZE = 100; // Limite du cache en mémoire
  const MAX_RETRIES = 3;

  // 🔍 Extraction des valeurs uniques pour les filtres dynamiques
  const updateAvailableFilterOptions = useCallback((revenues) => {
    console.log('🔍 Updating available filter options from revenues...');
    
    // Extraire les devises uniques
    const currencies = [...new Set(
      revenues
        .map(revenue => revenue.currency || 'EUR')
        .filter(Boolean)
    )].sort();
    
    // Extraire les méthodes de paiement uniques
    const paymentMethods = [...new Set(
      revenues.flatMap(revenue => 
        (revenue.payments_details || []).map(p => p.payment_method || 'unknown')
      ).filter(method => method && method !== 'unknown')
    )].sort();
    
    // Extraire les statuts de paiement uniques
    const paymentStatuses = [...new Set(
      revenues.flatMap(revenue => 
        (revenue.payments_details || []).map(p => p.status || 'pending')
      ).filter(Boolean)
    )].sort();
    
    console.log('💰 Available currencies:', currencies);
    console.log('💳 Available payment methods:', paymentMethods);
    console.log('📊 Available payment statuses:', paymentStatuses);
    
    setAvailableCurrencies(currencies);
    setAvailablePaymentMethods(paymentMethods);
    setAvailableStatuses(paymentStatuses);
  }, []);

  /**
   * Charge un chunk de revenus depuis le backend
   */
  const loadRevenuesChunk = useCallback(async (chunkPage = 1, reset = false, explicitFilters = null) => {
    if (loading) {
      console.log('⚠️ Already loading, skipping chunk request');
      return { hasMore: false, newRevenues: [], totalFromBackend: 0 };
    }
    
    setLoading(true);
    if (reset) setError(null);
    
    try {
      // Utiliser les filtres explicites s'ils sont fournis, sinon les filtres actifs
      const filtersToUse = explicitFilters !== null ? explicitFilters : activeFilters;
      
      console.log(`🔧 Loading OWNER REVENUE CHUNK: page ${chunkPage}, size ${BACKEND_CHUNK_SIZE}`, {
        activeFilters,
        propFilters: filters,
        explicitFilters,
        filtersToUse
      });
      
      // Annuler la requête précédente si elle existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const response = await paymentService.getOwnerRevenueHistory({
        page: chunkPage,
        limit: BACKEND_CHUNK_SIZE,
        ...filters,
        ...filtersToUse, // Utiliser les filtres appropriés
        component: 'OwnerPaymentHistoryOptimized'
      });
      
      if (!response?.success) throw new Error(response?.error || 'Failed to load owner revenues');
      
      const allReservations = response.data?.history || [];
      
      // Récupérer les valeurs de filtres disponibles depuis le backend
      if (response.data?.available_filter_values) {
        setAvailableFilterValues(response.data.available_filter_values);
      }
      
      // Traitement optimisé des données
      const processedRevenues = allReservations.map((item, index) => ({
        ...item,
        id: item.reservation_id || `owner-revenue-${chunkPage}-${index}`,
        // Transformation des données pour la vue owner
        total_amount: item.total_revenue || 0,
        net_amount: item.net_revenue || 0,
        commission_amount: item.commission_earned || 0,
        rental_amount: item.rental_revenue || 0,
        tax_amount: item.tax_revenue || 0,
        currency: item.currency || 'EUR',
        date: item.created_at || item.start_date,
        status: item.reservation_status || 'unknown',
        // Infos spécifiques owner
        listing_title: item.listing_title,
        renter_name: item.renter_name,
        renter_email: item.renter_email,
        payment_flags: item.payment_flags || { type: 'unknown', icon: '❓', label: 'Inconnu' },
        payments_details: item.payments || [],
        // Informations dépôt de garantie
        has_deposit: item.has_deposit || false,
        deposit_status: item.deposit_status || null,
        deposit_amount: item.deposit_amount || 0,
        deposit_details: item.deposit || null
      })).filter(revenue => revenue.id);
      
      const pagination = response.data?.pagination || {};
      const hasMoreChunks = Boolean(
        pagination.has_next_page || 
        pagination.hasNext || 
        processedRevenues.length === BACKEND_CHUNK_SIZE
      );
      
      console.log(`✅ Owner revenue chunk loaded: ${processedRevenues.length} revenues, hasMore: ${hasMoreChunks}`);
      
      retryCount.current = 0; // Reset retry count on success
      
      return {
        hasMore: hasMoreChunks,
        newRevenues: processedRevenues,
        totalFromBackend: pagination.total_items || 0
      };
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🚫 Owner revenue request aborted');
        return { hasMore: false, newRevenues: [], totalFromBackend: 0 };
      }
      
      console.error('❌ Failed to load owner revenue chunk:', error);
      
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current++;
        console.log(`🔄 Retrying owner revenue chunk load (${retryCount.current}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount.current));
        return loadRevenuesChunk(chunkPage, reset, explicitFilters);
      }
      
      setError(`Erreur lors du chargement des revenus: ${error.message}`);
      return { hasMore: false, newRevenues: [], totalFromBackend: 0 };
      
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [loading, filters, activeFilters]); // Ajouter activeFilters aux dépendances

  /**
   * Initialise ou recharge les données
   */
  const initializeOrRefresh = useCallback(async (isRefresh = false, explicitBackendFilters = null) => {
    console.log(`🚀 ${isRefresh ? 'Refreshing' : 'Initializing'} owner revenue history`);
    
    if (isRefresh) {
      setAllRevenues([]);
      setFilteredRevenues([]);
      setDisplayedRevenues([]);
      setCurrentPage(1);
      setBackendPage(1);
      setHasMoreBackend(true);
      setExpandedRevenues({});
    }
    
    setInitialLoading(!isRefresh);

    // Utiliser les filtres explicites si fournis, sinon ceux déjà en mémoire
    const backendFiltersToUse = explicitBackendFilters !== null ? explicitBackendFilters : (backendQueryRef.current || activeFilters);

    backendQueryRef.current = backendFiltersToUse || {};
    backendQueryKeyRef.current = serializeBackendQuery(backendQueryRef.current);
    setActiveFilters(backendQueryRef.current);

    const { hasMore, newRevenues, totalFromBackend } = await loadRevenuesChunk(1, true, backendQueryRef.current);
    
    setAllRevenues(newRevenues);
    setFilteredRevenues(newRevenues);
    setTotalItems(totalFromBackend);
    setHasMoreBackend(hasMore);
    setBackendPage(hasMore ? 2 : 1);
    
    // Afficher la première page
    const firstPage = newRevenues.slice(0, FRONTEND_PAGE_SIZE);
    setDisplayedRevenues(firstPage);
    
    setInitialLoading(false);
    
    console.log(`✅ Owner revenue history ${isRefresh ? 'refreshed' : 'initialized'}: ${newRevenues.length} items loaded`);
    
  }, [loadRevenuesChunk, activeFilters]);

  /**
   * Charge plus de revenus depuis le backend
   */
  const loadMoreFromBackend = useCallback(async () => {
    if (!hasMoreBackend || loading) {
      console.log('⚠️ Cannot load more from backend:', { hasMoreBackend, loading });
      return;
    }
    
    // Protection mémoire - ne pas charger si on atteint la limite
    if (allRevenues.length >= MAX_CACHE_SIZE) {
      console.log(`⚠️ CACHE LIMIT reached (${MAX_CACHE_SIZE}), stopping backend loading`);
      setHasMoreBackend(false);
      return;
    }
    
    const pageToLoad = backendPage;
    console.log(`📥 Loading MORE from backend: page ${pageToLoad}`);

    // Utiliser backendQueryRef pour conserver les filtres du backend
    const result = await loadRevenuesChunk(pageToLoad, false, backendQueryRef.current);
    
    if (result.newRevenues.length > 0) {
      setAllRevenues(prev => {
        const combined = [...prev, ...result.newRevenues];
        // Éliminer doublons et appliquer limite cache
        const unique = combined.filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i);
        const limited = unique.slice(0, MAX_CACHE_SIZE);
        
        console.log(`🔧 Backend merge: ${prev.length} + ${result.newRevenues.length} = ${limited.length} (limited)`);
        return limited;
      });
      setBackendPage(prev => prev + 1);
    }
    
    setHasMoreBackend(result.hasMore);
    
    if (result.totalFromBackend) {
      setTotalItems(result.totalFromBackend);
    }
  }, [hasMoreBackend, allRevenues.length, backendPage, loadRevenuesChunk, loading]);

  /**
   * Affiche plus d'items du cache filtré côté frontend (pagination)
   */
  const loadMoreDisplayed = useCallback(() => {
    const maxPage = Math.ceil(filteredRevenues.length / FRONTEND_PAGE_SIZE);
    
    if (currentPage >= maxPage) {
      console.log('ℹ️ No more filtered revenues to display');
      return;
    }
    
    const nextPage = currentPage + 1;
    console.log(`📥 Showing more DISPLAYED revenues (page ${nextPage})`);
    
    setCurrentPage(nextPage);
  }, [currentPage, filteredRevenues]);

  /**
   * Récupère le litige pour une réservation et les pénalités associées
   */
  const fetchDisputeForReservation = useCallback(async (reservationId) => {
    const fetchPenaltiesForDispute = async (dispute) => {
      const disputeId = getDisputeId(dispute);
      if (!disputeId) {
        setPenaltiesByReservation(prev => ({
          ...prev,
          [reservationId]: []
        }));
        return;
      }

      try {
        // NOTE: `api` baseURL already includes `/api/v1`.
        const penaltyResponse = await api.get('/penalties', {
          params: {
            source_type: 'dispute',
            source_id: disputeId
          }
        });

        if (penaltyResponse.data?.success && penaltyResponse.data?.data) {
          const penaltiesRaw = Array.isArray(penaltyResponse.data.data)
            ? penaltyResponse.data.data
            : [penaltyResponse.data.data];

          // Align with DisputeDetailPage: keep only applied/collecting penalties
          const penalties = penaltiesRaw.filter(p =>
            p?.status === 'applied' || p?.status === 'collected' || p?.status === 'pending_collection'
          );

          setPenaltiesByReservation(prev => ({
            ...prev,
            [reservationId]: penalties
          }));
        } else {
          setPenaltiesByReservation(prev => ({
            ...prev,
            [reservationId]: []
          }));
        }
      } catch (penaltyError) {
        console.error('Error fetching penalties:', penaltyError);
        setPenaltiesByReservation(prev => ({
          ...prev,
          [reservationId]: []
        }));
      }
    };

    // If dispute is already cached, still fetch penalties if not fetched yet.
    // Also allow one retry for resolved disputes with an empty list (useful after hot-reload
    // where previous code may have cached [] due to a bad URL).
    const cachedDispute = disputesByReservation[reservationId];
    if (cachedDispute) {
      const cachedPenalties = penaltiesByReservation[reservationId];
      const shouldFetchInitially = cachedPenalties === undefined;
      const shouldRetryOnce =
        Array.isArray(cachedPenalties) &&
        cachedPenalties.length === 0 &&
        cachedDispute?.status === 'resolved' &&
        !penaltiesRetryByReservation[reservationId];

      if (shouldRetryOnce) {
        setPenaltiesRetryByReservation(prev => ({
          ...prev,
          [reservationId]: true
        }));
      }

      if (shouldFetchInitially || shouldRetryOnce) {
        await fetchPenaltiesForDispute(cachedDispute);
      }
      return;
    }

    // Vérifier si le litige est déjà en cours de chargement
    if (loadingDisputes[reservationId]) {
      return;
    }

    setLoadingDisputes(prev => ({
      ...prev,
      [reservationId]: true
    }));

    try {
      const response = await api.get(`/disputes?reservation_id=${reservationId}`);
      
      if (response.data?.success && response.data?.data) {
        const disputes = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        const dispute = disputes.length > 0 ? disputes[0] : null;
        
        setDisputesByReservation(prev => ({
          ...prev,
          [reservationId]: dispute
        }));

        // Charger les pénalités associées à la dispute
        if (dispute) {
          await fetchPenaltiesForDispute(dispute);
        } else {
          setPenaltiesByReservation(prev => ({
            ...prev,
            [reservationId]: []
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching dispute:', error);
      setDisputesByReservation(prev => ({
        ...prev,
        [reservationId]: null
      }));
      setPenaltiesByReservation(prev => ({
        ...prev,
        [reservationId]: []
      }));
    } finally {
      setLoadingDisputes(prev => ({
        ...prev,
        [reservationId]: false
      }));
    }
  }, [disputesByReservation, penaltiesByReservation, penaltiesRetryByReservation, loadingDisputes]);

  /**
   * Gère l'expansion des détails d'un revenu
   */
  const handleToggleExpansion = useCallback((revenueId, reservationId) => {
    setExpandedRevenues(prev => {
      const newExpanded = {
        ...prev,
        [revenueId]: !prev[revenueId]
      };

      // Fetch dispute when expanding
      if (newExpanded[revenueId] && reservationId) {
        fetchDisputeForReservation(reservationId);
      }

      return newExpanded;
    });
  }, [fetchDisputeForReservation]);

  /**
   * Gère l'ouverture du modal de détails de réservation
   */
  const handleShowReservationDetails = useCallback((reservationId) => {
    setSelectedReservationId(reservationId);
    setIsModalOpen(true);
  }, []);

  /**
   * Gère la fermeture du modal
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedReservationId(null);
  }, []);

  /**
   * Gestionnaire de changement de filtres
   */
  const handleFiltersChange = useCallback(async (newFilters) => {
    console.log('🔍 Filters changed:', newFilters);
    
    // Stocker la requête backend courante pour loadMoreFromBackend
    backendQueryRef.current = newFilters;
    backendQueryKeyRef.current = serializeBackendQuery(newFilters);
    setActiveFilters(newFilters);
    
    // Réinitialiser et recharger avec les nouveaux filtres
    setAllRevenues([]);
    setFilteredRevenues([]);
    setDisplayedRevenues([]);
    setCurrentPage(1);
    setBackendPage(1);
    setHasMoreBackend(true);
    setExpandedRevenues({});
    
    // Recharger les données avec les nouveaux filtres en utilisant loadRevenuesChunk directement
    setInitialLoading(true);
    
    // IMPORTANT: Passer newFilters explicitement car setActiveFilters est asynchrone
    const { hasMore, newRevenues, totalFromBackend } = await loadRevenuesChunk(1, true, newFilters);
    
    setAllRevenues(newRevenues);
    setFilteredRevenues(newRevenues);
    setTotalItems(totalFromBackend);
    setHasMoreBackend(hasMore);
    setBackendPage(hasMore ? 2 : 1);
    
    // Afficher la première page
    const firstPage = newRevenues.slice(0, FRONTEND_PAGE_SIZE);
    setDisplayedRevenues(firstPage);
    
    setInitialLoading(false);
    
    console.log(`✅ Filters applied and data reloaded: ${newRevenues.length} items loaded`);
  }, [loadRevenuesChunk]);

  // 🔧 Gestionnaires d'événements
  const handleFilterChange = (field, value) => {
    console.log(`🔧 Filter changed: ${field} = ${value}`);
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ⚡ Update dates en UNE SEULE FOIS (evite render cascade)
  const handleDateRangeChange = (start, end) => {
    setLocalFilters(prev => ({
      ...prev,
      dateFrom: start,
      dateTo: end
    }));
  };

  const handleSort = (field) => {
    console.log(`🔧 Sort changed: ${field}`);
    if (sortBy === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // 🔧 Gestion du bouton "Scroll to Top"
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    initializeOrRefresh(true, backendQueryRef.current);
  };

  // Refs pour tracker les filtres précédents
  const prevLocalFiltersRef = useRef(localFilters);

  // 🔄 Effet pour appliquer les filtres locaux sur les revenus en cache
  useEffect(() => {
    console.log('🔍 Applying local filters to cached revenues...');
    
    // Mettre à jour les options de filtres disponibles
    updateAvailableFilterOptions(allRevenues);
    
    // Appliquer les filtres locaux
    const filtered = allRevenues.filter(revenue => {
      // Filtre par statut de réservation
      if (localFilters.reservationStatus && localFilters.reservationStatus !== '') {
        const reservationStatus = String(revenue.status || 'pending').toLowerCase();
        const filterReservationStatus = String(localFilters.reservationStatus).toLowerCase();
        if (reservationStatus !== filterReservationStatus) return false;
      }
      
      // Filtre par devise
      if (localFilters.currency && localFilters.currency !== '') {
        const currency = String(revenue.currency || 'EUR').toUpperCase();
        const filterCurrency = String(localFilters.currency).toUpperCase();
        if (currency !== filterCurrency) return false;
      }
      
      // Filtre par méthode de paiement
      if (localFilters.paymentMethod && localFilters.paymentMethod !== '') {
        const filterMethod = String(localFilters.paymentMethod).toLowerCase();
        const hasPaymentMethod = (revenue.payments_details || []).some(p => {
          const method = String(p.payment_method || 'unknown').toLowerCase();
          if (filterMethod === 'stripe' && !['stripe', 'card'].includes(method)) return false;
          if (filterMethod === 'cash' && method !== 'cash') return false;
          if (filterMethod === 'virement' && method !== 'virement') return false;
          return method === filterMethod;
        });
        if (!hasPaymentMethod) return false;
      }
      
      // Filtre par statut de paiement
      if (localFilters.status && localFilters.status !== '') {
        const filterPaymentStatus = String(localFilters.status).toLowerCase();
        const hasPaymentStatus = (revenue.payments_details || []).some(p => String(p.status || '').toLowerCase() === filterPaymentStatus);
        if (!hasPaymentStatus) return false;
      }
      
      // Filtre par plage de dates
      if (localFilters.dateFrom || localFilters.dateTo) {
        const revenueDate = new Date(revenue.date || revenue.created_at);
        
        if (localFilters.dateFrom) {
          const fromDate = new Date(localFilters.dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (revenueDate < fromDate) return false;
        }
        
        if (localFilters.dateTo) {
          const toDate = new Date(localFilters.dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (revenueDate > toDate) return false;
        }
      }
      
      return true;
    });
    
    setFilteredRevenues(filtered);
    
    // Reset currentPage SEULEMENT si les localFilters ont changé (pas si allRevenues a changé)
    if (JSON.stringify(prevLocalFiltersRef.current) !== JSON.stringify(localFilters)) {
      setCurrentPage(1);
      console.log('🔄 Local filters changed, resetting to page 1');
    }
    
    prevLocalFiltersRef.current = localFilters;
    
    console.log(`🔍 Filtering result: ${allRevenues.length} → ${filtered.length} revenues`);
  }, [allRevenues, localFilters, updateAvailableFilterOptions]);

  // Reload backend quand les filtres BACKEND changent (paymentMethod/reservationStatus/dateRange)
  useEffect(() => {
    if (!hasInitialized.current) return;
    if (disableAutoLoad) return;

    const nextBackendQuery = buildBackendQueryFromLocalFilters(localFilters);
    const nextKey = serializeBackendQuery(nextBackendQuery);

    if (nextKey === backendQueryKeyRef.current) return;

    console.log('🔄 Backend filters changed -> reloading from page 1', nextBackendQuery);
    handleFiltersChange(nextBackendQuery);
  }, [localFilters, disableAutoLoad, handleFiltersChange]);

  // 📄 Effet pour appliquer la pagination ET le tri sur les revenus filtrés
  useEffect(() => {
    console.log('🔄 Applying pagination AND sort to filtered revenues...');
    const paginated = filteredRevenues.slice(0, currentPage * FRONTEND_PAGE_SIZE);
    
    const sorted = [...paginated].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.date || a.created_at || 0);
          valueB = new Date(b.date || b.created_at || 0);
          break;
        case 'amount':
          valueA = parseFloat(a.total_amount || a.net_amount || 0);
          valueB = parseFloat(b.total_amount || b.net_amount || 0);
          break;
        default:
          return 0;
      }
      
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setDisplayedRevenues(sorted);
    console.log(`📊 Sort applied: showing ${sorted.length} sorted revenues`);
  }, [filteredRevenues, currentPage, sortBy, sortDirection]);

  // Initialisation au montage
  useEffect(() => {
    if (!hasInitialized.current && !disableAutoLoad) {
      const initialBackendQuery = buildBackendQueryFromLocalFilters(localFilters);
      backendQueryRef.current = initialBackendQuery;
      backendQueryKeyRef.current = serializeBackendQuery(initialBackendQuery);
      setActiveFilters(initialBackendQuery);

      hasInitialized.current = true;
      initializeOrRefresh(false, initialBackendQuery);
    }
    
    // Cleanup
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [initializeOrRefresh, disableAutoLoad, localFilters]);

  // Exposition des méthodes via ref
  React.useImperativeHandle(ref, () => ({
    refresh: () => initializeOrRefresh(true, backendQueryRef.current),
    loadMore: loadMoreFromBackend,
    loadMoreDisplayed: loadMoreDisplayed,
    getDisplayedCount: () => displayedRevenues.length,
    getTotalCount: () => totalItems,
    getCacheSize: () => allRevenues.length,
    getFilteredCount: () => filteredRevenues.length
  }));

  // États dérivés - logique de pagination alignée sur le Renter
  const canLoadMoreFrontend = displayedRevenues.length < filteredRevenues.length && !loading && !initialLoading;
  const needsMoreFromBackend = displayedRevenues.length >= filteredRevenues.length && hasMoreBackend && allRevenues.length < MAX_CACHE_SIZE && !loading;
  const isEndReached = displayedRevenues.length > 0 && displayedRevenues.length >= filteredRevenues.length && !hasMoreBackend;

  // Affichage de chargement initial
  if (initialLoading) {
    return (
      <Box className="payment-history-infinite">
        <Box display="flex" flexDirection="column" alignItems="center" p={4}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
            {t('financial:owner.loadingRevenues', 'Chargement des revenus...')}
          </Typography>
          <LinearProgress sx={{ width: '100%', mt: 2 }} />
        </Box>
      </Box>
    );
  }



  return (
    <Box className="payment-history-infinite">
      {/* En-tête avec boutons filtres et tri */}
      <Box 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}
      >
        <Typography variant="h6" sx={{ mb: { xs: 2, sm: 0 } }}>
          💰 {t('financial:owner.revenueHistory', 'Historique des Revenus')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterIcon />}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            size="small"
          >
            {isFilterOpen ? t('financial:payment.hideFilters', 'Masquer les filtres') : t('financial:payment.showFilters', 'Filtrer')}
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SortIcon />}
            size="small"
            onClick={() => handleSort(sortBy === 'date' ? 'amount' : 'date')}
          >
            {sortBy === 'date' ? t('financial:payment.sortByAmount', 'Trier par montant') : t('financial:payment.sortByDate', 'Trier par date')}
          </Button>
        </Box>
      </Box>

      {/* Filtres avancés */}
      <Collapse in={isFilterOpen}>
        <Card sx={{ p: 2, mb: 3, bgcolor: alpha('#000', 0.02) }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            🔍 Filtres avancés
          </Typography>
          
          <Grid container spacing={2}>
            {/* Statut de réservation */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="reservation-status-filter-label">{t('financial:payment.filters.reservationStatus', 'Statut')}</InputLabel>
                <Select
                  labelId="reservation-status-filter-label"
                  value={localFilters.reservationStatus}
                  onChange={(e) => handleFilterChange('reservationStatus', e.target.value)}
                  label={t('financial:payment.filters.reservationStatus', 'Statut')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allStatuses', 'Tous les statuts')}</MenuItem>
                  <MenuItem value="pending">{t('financial:renter.reservationStatus.pending', '⌛ En attente')}</MenuItem>
                  <MenuItem value="confirmed">{t('financial:renter.reservationStatus.confirmed', '✓ Confirmée')}</MenuItem>
                  <MenuItem value="active">{t('financial:renter.reservationStatus.active', '🔵 En cours')}</MenuItem>
                  <MenuItem value="completed">{t('financial:renter.reservationStatus.completed', '✅ Terminée')}</MenuItem>
                  <MenuItem value="cancelled">{t('financial:renter.reservationStatus.cancelled', '❌ Annulée')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Statut de paiement */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="payment-status-filter-label">{t('financial:payment.filters.paymentStatus', 'Statut paiement')}</InputLabel>
                <Select
                  labelId="payment-status-filter-label"
                  value={localFilters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label={t('financial:payment.filters.paymentStatus', 'Statut paiement')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allStatuses', 'Tous les statuts')}</MenuItem>
                  {availableStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Méthode de paiement */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="payment-method-filter-label">{t('financial:payment.filters.method', 'Méthode')}</InputLabel>
                <Select
                  labelId="payment-method-filter-label"
                  value={localFilters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  label={t('financial:payment.filters.method', 'Méthode')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allMethods', 'Toutes')}</MenuItem>
                  {availablePaymentMethods.map(method => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Devise */}
            <Grid item xs={6} sm={3} md={1.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="currency-filter-label">{t('financial:payment.filters.currency', 'Devise')}</InputLabel>
                <Select
                  labelId="currency-filter-label"
                  value={localFilters.currency}
                  onChange={(e) => handleFilterChange('currency', e.target.value)}
                  label={t('financial:payment.filters.currency', 'Devise')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allCurrencies', 'Toutes')}</MenuItem>
                  {availableCurrencies.map(currency => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Plage de dates - DateRangePicker */}
            <Grid item xs={12} sm={4} md={2.8}>
              <DateRangePicker
                startDate={localFilters.dateFrom}
                endDate={localFilters.dateTo}
                onDateRangeChange={handleDateRangeChange}
                label={t('financial:payment.filters.dateRange', 'Période')}
                placeholder="jj/mm/aaaa → jj/mm/aaaa"
                size="small"
              />
            </Grid>
          </Grid>
          
          {/* Boutons d'actions pour les filtres */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setLocalFilters({
                  status: '',
                  paymentMethod: '',
                  paymentType: '',
                  currency: '',
                  reservationStatus: '',
                  dateFrom: '',
                  dateTo: ''
                });
              }}
            >
              {t('financial:payment.filters.clearFilters', 'Effacer les filtres')}
            </Button>
            
            {/* Raccourcis de plages de dates */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                handleDateRangeChange(weekAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]);
              }}
            >
              {t('financial:payment.filters.last7Days', '7 derniers jours')}
            </Button>
            
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                handleDateRangeChange(monthAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]);
              }}
            >
              {t('financial:payment.filters.last30Days', '30 derniers jours')}
            </Button>
            
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                handleDateRangeChange(firstDay.toISOString().split('T')[0], today.toISOString().split('T')[0]);
              }}
            >
              {t('financial:payment.filters.thisMonth', 'Ce mois')}
            </Button>
          </Box>
        </Card>
      </Collapse>

      {/* En-tête avec statistiques */}
      <Box className="payment-history-header">
        <Box>
          <Typography variant="h6" component="h3">
            💰 {t('financial:owner.revenueHistory', 'Historique des Revenus')}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {t('financial:owner.revenueDesc', 'Revenus générés par vos listings')}
          </Typography>
        </Box>
        <Box className="history-count">
          {displayedRevenues.length} / {totalItems}
        </Box>
      </Box>

      {/* Affichage d'erreur */}
      {error && displayedRevenues.length === 0 && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={() => initializeOrRefresh(true)}>
              {t('common:retry', 'Réessayer')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Message de chargement */}
      {loading && displayedRevenues.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={32} />
          <Typography sx={{ mt: 2 }}>{t('financial:payment.loadingHistory', '🔄 Chargement...')}</Typography>
        </Box>
      )}

      {/* Message "Aucun résultat" */}
      {!loading && displayedRevenues.length === 0 && !error && (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: theme.palette.grey[50] }}>
          <MoneyIcon sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('financial:owner.noRevenues', 'Aucun revenu trouvé')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {localFilters.reservationStatus || localFilters.status || localFilters.paymentMethod || localFilters.currency || localFilters.dateFrom || localFilters.dateTo
              ? t('financial:payment.noMatchingReservations', '🔍 Aucun revenu ne correspond aux critères de filtrage.')
              : t('financial:owner.noRevenuesDesc', 'Vos revenus apparaîtront ici une fois que vous aurez des réservations confirmées.')
            }
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={() => initializeOrRefresh(true)}
            sx={{ mt: 1 }}
          >
            {t('common:refresh', 'Actualiser')}
          </Button>
        </Paper>
      )}

      {/* Liste des revenus */}
      {displayedRevenues.length > 0 && (
        <>
          <Box className="payment-history-list">
        {displayedRevenues.map((revenue, index) => (
          <Card key={revenue.id} className={`payment-history-item ${expandedRevenues[revenue.id] ? 'expanded' : ''}`}>
            {/* En-tête du revenu */}
            <Box className="payment-history-header-item" onClick={() => handleToggleExpansion(revenue.id, revenue.reservation_id)}>
              <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                <MoneyIcon />
              </Avatar>
              
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {revenue.listing_title || t('financial:listingUnknown', 'Listing inconnu')}
                    </Typography>
                    {/* Date de création de la réservation (préférer la date de réservation, fallback dépôt) */}
                    {(() => {
                      const createdAt = revenue.created_at || revenue.deposit_details?.created_at;
                      if (!createdAt) return null;
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                          {t('financial:dispute.createdAt', 'Créé le')} {formatDateWithTime(createdAt, t)}
                        </Typography>
                      );
                    })()}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      icon={<span>{revenue.payment_flags.icon}</span>}
                      label={revenue.payment_flags.label}
                      size="small"
                      variant="outlined"
                    />
                    {revenue.has_deposit && revenue.deposit_amount > 0 && (
                      <Chip 
                        label={t('financial:depositLabel', { amount: formatCurrency(revenue.deposit_amount, revenue.currency), defaultValue: `Dépôt: ${formatCurrency(revenue.deposit_amount, revenue.currency)}` })}
                        size="small"
                        variant="outlined"
                        color={
                          revenue.deposit_status === 'RELEASED' ? 'success' :
                          revenue.deposit_status === 'HELD' ? 'warning' :
                          revenue.deposit_status === 'COLLECTED' ? 'error' :
                          'default'
                        }
                      />
                    )}
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {formatCurrency(revenue.total_amount, revenue.currency)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      icon={getReservationStatusProps(revenue.status).icon}
                      label={getReservationStatusLabel(revenue.status, t)}
                      size="small"
                      color={getReservationStatusProps(revenue.status).color}
                    />
                    <Typography variant="caption" color="text.secondary">
                      <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} />
                      {revenue.renter_name || t('financial:renterUnknown', 'Locataire inconnu')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <IconButton size="small">
                {expandedRevenues[revenue.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* Détails expansés */}
            <Collapse in={expandedRevenues[revenue.id]}>
              <CardContent sx={{ pt: 0 }}>
                <Divider sx={{ mb: 2 }} />
                
                {/* Breakdown financier owner */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('financial:owner.breakdown.rental', 'Location')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(revenue.rental_amount, revenue.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('financial:owner.breakdown.tax', 'Taxes')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(revenue.tax_amount, revenue.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('financial:owner.breakdown.commission', 'Commission')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" color="warning.main">
                      -{formatCurrency(revenue.commission_amount, revenue.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('financial:owner.breakdown.net', 'Net reçu')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(revenue.net_amount, revenue.currency)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Informations dépôt de garantie */}
                {revenue.has_deposit && revenue.deposit_amount > 0 && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('financial:deposit.title', 'Dépôt de garantie')}:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          {t('financial:deposit.amount', 'Montant')}:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(revenue.deposit_amount, revenue.currency)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">
                          {t('table.status', 'Statut')}:
                        </Typography>
                        <Chip
                          icon={getDepositStatusProps(revenue.deposit_status).icon}
                          label={getDepositStatusLabel(revenue.deposit_status, t)}
                          size="small"
                          color={getDepositStatusProps(revenue.deposit_status).color}
                          sx={{ ml: 0.5 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {(!revenue.has_deposit || revenue.deposit_amount <= 0) && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      {t('financial:deposit.none', 'Aucun dépôt de garantie pour cette réservation')}
                    </Typography>
                  </Box>
                )}

                {/* Dispute Section */}
                {disputesByReservation[revenue.reservation_id] && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GavelIcon sx={{ color: 'warning.main' }} />
                        <Typography variant="subtitle2" fontWeight="600">
                          {t('financial:dispute.title', 'Litige')}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        href={`/disputes/${getDisputeId(disputesByReservation[revenue.reservation_id])}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="text"
                      >
                        {t('common:viewDetails', 'Voir')}
                      </Button>
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('financial:dispute.status', 'Statut')}:
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip 
                            label={getDisputeStatusLabel(disputesByReservation[revenue.reservation_id].status, t)} 
                            size="small"
                            color={getDisputeStatusColor(disputesByReservation[revenue.reservation_id].status)}
                          />
                        </Box>
                      </Grid>

                      {/* Show additional details only if resolved */}
                      {disputesByReservation[revenue.reservation_id].status === 'resolved' && (
                        <>
                          {disputesByReservation[revenue.reservation_id].compensation_amount && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                {t('financial:dispute.compensationAmount', 'Montant de compensation')}:
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                {formatCurrency(
                                  disputesByReservation[revenue.reservation_id].compensation_amount,
                                  revenue.currency
                                )}
                              </Typography>
                            </Grid>
                          )}

                          {disputesByReservation[revenue.reservation_id].compensation_type && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                {t('financial:dispute.compensationType', 'Type de compensation')}:
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                {getCompensationTypeLabel(disputesByReservation[revenue.reservation_id].compensation_type, t)}
                              </Typography>
                            </Grid>
                          )}

                          {disputesByReservation[revenue.reservation_id].created_at && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                {t('financial:dispute.createdAt', 'Créé le')}:
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                {formatDateTimeUniversal(disputesByReservation[revenue.reservation_id].created_at)}
                              </Typography>
                            </Grid>
                          )}

                          {disputesByReservation[revenue.reservation_id].updated_at && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                {t('financial:dispute.updatedAt', 'Mis à jour le')}:
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                {formatDateTimeUniversal(disputesByReservation[revenue.reservation_id].updated_at)}
                              </Typography>
                            </Grid>
                          )}

                          {disputesByReservation[revenue.reservation_id].resolved_at && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                {t('financial:dispute.resolvedAt', 'Résolu le')}:
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                {formatDateTimeUniversal(disputesByReservation[revenue.reservation_id].resolved_at)}
                              </Typography>
                            </Grid>
                          )}

                          {/* Display adjusted net amount if there's a compensation type */}
                          {disputesByReservation[revenue.reservation_id].compensation_type && (
                            <Grid item xs={12}>
                              <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  {t('financial:dispute.adjustedNetAmount', 'Net reçu ajusté')}:
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  fontWeight="bold"
                                  sx={{ 
                                    color: calculateAdjustedNetAmount(
                                      revenue.net_amount,
                                      disputesByReservation[revenue.reservation_id],
                                      penaltiesByReservation[revenue.reservation_id],
                                      currentUser?.id
                                    ) >= 0 
                                      ? 'success.main' 
                                      : 'error.main'
                                  }}
                                >
                                  {formatCurrency(
                                    calculateAdjustedNetAmount(
                                      revenue.net_amount,
                                      disputesByReservation[revenue.reservation_id],
                                      penaltiesByReservation[revenue.reservation_id],
                                      currentUser?.id
                                    ),
                                    revenue.currency
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  ({t(`financial:dispute.adjustmentExplanation.${disputesByReservation[revenue.reservation_id].compensation_type}`, 
                                    disputesByReservation[revenue.reservation_id].compensation_type === 'owner_compensation' 
                                      ? 'Base + Compensation' 
                                      : disputesByReservation[revenue.reservation_id].compensation_type === 'renter_refund'
                                        ? 'Base - Remboursement'
                                        : 'Aucun bénéfice'
                                  )})
                                </Typography>
                                {calculatePenaltyImpactForUser(
                                  penaltiesByReservation[revenue.reservation_id],
                                  currentUser?.id
                                ).net !== 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {calculatePenaltyImpactForUser(
                                      penaltiesByReservation[revenue.reservation_id],
                                      currentUser?.id
                                    ).net > 0
                                      ? t('financial:dispute.penaltyImpactPositive', 'Impact pénalités inclus: compensation reçue')
                                      : t('financial:dispute.penaltyImpactNegative', 'Impact pénalités inclus: déduction appliquée')}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>
                  </Box>
                )}

                {loadingDisputes[revenue.reservation_id] && (
                  <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {t('financial:dispute.loadingDispute', 'Chargement du litige...')}
                    </Typography>
                  </Box>
                )}

                {/* Cancelled Reservation Section - Show zero net if no dispute */}
                {!disputesByReservation[revenue.reservation_id] && revenue.status === 'cancelled' && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('financial:dispute.adjustedNetAmount', 'Net reçu ajusté')}:
                      </Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        sx={{ color: 'error.main' }}
                      >
                        {formatCurrency(0, revenue.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        ({t('financial:reservationStatus.cancelledNoBenefit', 'Réservation annulée - Aucun bénéfice')})
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Penalty Execution Summary Section */}
                {penaltiesByReservation[revenue.reservation_id] && penaltiesByReservation[revenue.reservation_id].length > 0 && currentUser && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    {penaltiesByReservation[revenue.reservation_id]
                      .filter(p => p?.user_id === currentUser.id || p?.execution_metadata?.victim_compensation?.victim_user_id === currentUser.id)
                      .map(p => (
                        <Accordion key={p.id} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {t('financial:types.penalty', 'Pénalités')}
                                  </Typography>
                                  <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', mx: 1 }} />
                                  <Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                    {`#${String(p.id)}`}
                                  </Typography>
                                </Box>
                                <Chip label={`${formatCurrency(p.amount || p.execution_metadata?.total_collected || 0, revenue.currency)}`} size="small" color="error" />
                                <Chip label={t(`disputes:penalties.execution.statuses.${p.status}`, p.status)} size="small" variant="outlined" />
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {p.applied_at ? formatDateTimeUniversal(p.applied_at) : (p.created_at ? formatDateTimeUniversal(p.created_at) : '')}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <PenaltyExecutionSummary penalty={p} dispute={disputesByReservation[revenue.reservation_id]} />
                          </AccordionDetails>
                        </Accordion>
                    ))}
                  </Box>
                )}

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleShowReservationDetails(revenue.reservation_id)}
                  >
                    {t('financial:viewDetails', 'Voir détails')}
                  </Button>
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        ))}
      </Box>

      {/* Section des boutons d'action */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2, gap: 2, flexWrap: 'wrap' }}>
        {/* Bouton "Load More" - Affiche plus de résultats filtrés */}
        {canLoadMoreFrontend && (
          <Button
            variant="contained"
            color="primary"
            onClick={loadMoreDisplayed}
            startIcon={<ExpandMoreIcon />}
            sx={{ borderRadius: 2, px: 3 }}
            disabled={loading}
          >
            {t('financial:payment.loadMoreReservations', 'Charger plus de revenus')} ({displayedRevenues.length}/{filteredRevenues.length})
          </Button>
        )}
        
        {/* Bouton pour charger plus de données backend */}
        {needsMoreFromBackend && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={loadMoreFromBackend}
            startIcon={<ExpandMoreIcon />}
            sx={{ borderRadius: 2, px: 3 }}
            disabled={loading}
          >
            {t('financial:payment.loadMoreFromServer', 'Charger plus depuis le serveur')} ({allRevenues.length}/{totalItems})
          </Button>
        )}
        
        {/* Message si toutes les réservations filtrées sont affichées */}
        {isEndReached && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {t('financial:payment.allDisplayed', '✅ Tous les revenus correspondants sont affichés')} ({displayedRevenues.length}/{filteredRevenues.length})
          </Typography>
        )}
        
        {/* Bouton "Refresh" */}
        <Button
          variant="outlined"
          color="primary"
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
          sx={{ borderRadius: 2, px: 3 }}
          disabled={loading || initialLoading}
        >
          {t('common:refresh', 'Actualiser')}
        </Button>
      </Box>
          </>
        )}

      {/* Informations de performance */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 1 }}>
        <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
          ⚡ Cache: {allRevenues.length} revenus | 📄 Affichés: {displayedRevenues.length}
          {hasMoreBackend && ` | 📡 Plus de données disponibles`}
        </Typography>
      </Box>

      {/* Bouton Scroll to Top flottant */}
      {showScrollToTop && (
        <Box
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 3,
            zIndex: 1000,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)',
              boxShadow: 6,
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
        >
          ↑
        </Box>
      )}

      {/* Modal de détails de réservation */}
      <ReservationDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        reservationId={selectedReservationId}
      />
    </Box>
  );
});

export default OwnerPaymentHistoryInfinite;
